import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Eye, EyeSlash, ArrowUp, ArrowDown, ImageSquare, FloppyDisk, Globe,
  Copy, CheckCircle, Trash, Plus, LinkSimple, X,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTION_META = {
  hero:        { label: 'Hero',        order: 0 },
  problem:     { label: 'Problem',     order: 1 },
  solution:    { label: 'Solution',    order: 2 },
  features:    { label: 'Features',    order: 3 },
  social_proof:{ label: 'Testimonials',order: 4 },
  pricing:     { label: 'Pricing',     order: 5 },
  cta:         { label: 'Call to Action', order: 6 },
  faq:         { label: 'FAQ',         order: 7 },
};

/* ----- inline editable text ----- */
const InlineText = ({ value, onChange, multiline = false, placeholder, className = '', as = 'span', testid }) => {
  const Tag = as;
  const ref = useRef(null);

  const onBlur = () => {
    const v = ref.current?.innerText?.replace(/\s+$/, '') || '';
    if (v !== value) onChange(v);
  };

  // keep DOM in sync when value changes externally (e.g., regeneration)
  useEffect(() => {
    if (ref.current && ref.current.innerText !== (value || '')) {
      ref.current.innerText = value || '';
    }
  }, [value]);

  return (
    <Tag
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      data-testid={testid}
      onBlur={onBlur}
      onKeyDown={e => { if (!multiline && e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
      className={`outline-none focus:ring-2 focus:ring-[#c8f060]/40 focus:ring-offset-2 focus:ring-offset-transparent rounded transition-all hover:bg-white/[0.03] ${className}`}
      style={{ minHeight: '1em', cursor: 'text' }}
    >
      {value || ''}
    </Tag>
  );
};

/* ----- image upload swatch ----- */
const ImageUpload = ({ url, onChange, label = 'Add image', testid }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/api/uploads/file`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(`${API}${data.url}`);
      toast.success('Image uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  if (url) {
    return (
      <div className="relative inline-block">
        <img src={url} alt="" className="max-w-full rounded-xl" style={{ maxHeight: 220 }} />
        <div className="absolute top-2 right-2 flex gap-1">
          <button onClick={() => inputRef.current?.click()} className="p-1.5 rounded-md bg-black/60 backdrop-blur text-white" title="Replace" data-testid={`${testid}-replace`}>
            <ImageSquare size={12} weight="bold" />
          </button>
          <button onClick={() => onChange('')} className="p-1.5 rounded-md bg-black/60 backdrop-blur text-white" title="Remove" data-testid={`${testid}-remove`}>
            <X size={12} weight="bold" />
          </button>
        </div>
        <input ref={inputRef} type="file" accept="image/*,video/mp4" hidden onChange={e => upload(e.target.files?.[0])} />
      </div>
    );
  }

  return (
    <button onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={testid}
      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border border-dashed border-white/15 text-white/60 hover:text-white hover:border-white/30 transition-all disabled:opacity-50">
      <ImageSquare size={12} weight="bold" /> {uploading ? 'Uploading…' : label}
      <input ref={inputRef} type="file" accept="image/*,video/mp4" hidden onChange={e => upload(e.target.files?.[0])} />
    </button>
  );
};

/* ----- section wrapper with controls ----- */
const SectionShell = ({ name, section, onUpdate, onMove, onToggleVisible, isFirst, isLast, children }) => {
  const visible = section?.visible !== false;
  return (
    <div className="rounded-2xl overflow-hidden border" data-testid={`section-${name}`}
         style={{ background: visible ? '#0f0f0f' : '#0a0a0a', borderColor: visible ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.03)', opacity: visible ? 1 : 0.55 }}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.04] bg-white/[0.02]">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: 'rgba(245,240,232,0.4)' }}>{SECTION_META[name]?.label || name}</span>
          {!visible && <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(245,240,232,0.5)' }}>Hidden</span>}
        </div>
        <div className="flex items-center gap-0.5">
          <button onClick={() => onMove(name, -1)} disabled={isFirst} data-testid={`section-${name}-up`} className="p-1.5 rounded-md hover:bg-white/5 disabled:opacity-30" title="Move up"><ArrowUp size={12} /></button>
          <button onClick={() => onMove(name, 1)} disabled={isLast} data-testid={`section-${name}-down`} className="p-1.5 rounded-md hover:bg-white/5 disabled:opacity-30" title="Move down"><ArrowDown size={12} /></button>
          <button onClick={() => onToggleVisible(name)} data-testid={`section-${name}-toggle`} className="p-1.5 rounded-md hover:bg-white/5" title={visible ? 'Hide section' : 'Show section'}>
            {visible ? <Eye size={12} /> : <EyeSlash size={12} />}
          </button>
        </div>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
};

/* ----- per-section editors ----- */
const HeroEditor = ({ data, set }) => (
  <div className="space-y-4 text-center">
    <InlineText value={data.headline} onChange={v => set({ ...data, headline: v })} testid="hero-headline"
      className="block text-3xl sm:text-4xl font-bold tracking-tight text-white" placeholder="Hero headline" as="h1" />
    <InlineText value={data.subheadline} onChange={v => set({ ...data, subheadline: v })} multiline testid="hero-subheadline"
      className="block text-base text-white/60 max-w-xl mx-auto" placeholder="Subheadline" as="p" />
    <ImageUpload url={data.image_url || ''} onChange={v => set({ ...data, image_url: v })} testid="hero-image" />
    <div className="flex flex-col items-center gap-2 mt-2">
      <InlineText value={data.cta_text} onChange={v => set({ ...data, cta_text: v })} testid="hero-cta-label"
        className="inline-block px-6 py-2.5 rounded-full text-sm font-semibold" placeholder="Button text"
        style={{ background: '#c8f060', color: '#080808' }} as="span" />
      <div className="flex items-center gap-1.5 text-[11px] text-white/40">
        <LinkSimple size={11} />
        <InlineText value={data.cta_url} onChange={v => set({ ...data, cta_url: v })} testid="hero-cta-url"
          className="text-[11px] text-white/60" placeholder="https://your-link.com" as="span" />
      </div>
    </div>
  </div>
);

const ProblemEditor = ({ data, set }) => {
  const updatePain = (i, v) => {
    const pp = [...(data.pain_points || [])];
    pp[i] = v;
    set({ ...data, pain_points: pp });
  };
  const addPain = () => set({ ...data, pain_points: [...(data.pain_points || []), 'New pain point'] });
  const removePain = i => set({ ...data, pain_points: (data.pain_points || []).filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-3">
      <InlineText value={data.headline} onChange={v => set({ ...data, headline: v })} testid="problem-headline"
        className="block text-2xl font-bold text-white" placeholder="Problem headline" as="h2" />
      <InlineText value={data.description} onChange={v => set({ ...data, description: v })} multiline testid="problem-description"
        className="block text-sm text-white/60" placeholder="Description" as="p" />
      <ul className="space-y-1.5">
        {(data.pain_points || []).map((pp, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: '#c8f060' }} />
            <InlineText value={pp} onChange={v => updatePain(i, v)} className="flex-1 text-sm text-white/80" as="span" />
            <button onClick={() => removePain(i)} className="p-1 rounded hover:bg-white/5 opacity-50 hover:opacity-100"><Trash size={11} style={{ color: '#ff6b6b' }} /></button>
          </li>
        ))}
      </ul>
      <button onClick={addPain} className="text-[11px] flex items-center gap-1 px-2 py-1 rounded hover:bg-white/5" style={{ color: 'rgba(245,240,232,0.5)' }}>
        <Plus size={10} /> Add pain point
      </button>
    </div>
  );
};

const SolutionEditor = ({ data, set }) => (
  <div className="space-y-3">
    <InlineText value={data.headline} onChange={v => set({ ...data, headline: v })} className="block text-2xl font-bold text-white" as="h2" placeholder="Solution headline" />
    <InlineText value={data.description} onChange={v => set({ ...data, description: v })} multiline className="block text-sm text-white/60" as="p" placeholder="Description" />
    <InlineText value={data.key_differentiator} onChange={v => set({ ...data, key_differentiator: v })} className="block text-sm font-semibold" style={{ color: '#c8f060' }} as="p" placeholder="Key differentiator" />
  </div>
);

const FeaturesEditor = ({ data, set }) => {
  const updateItem = (i, key, v) => {
    const items = [...(data.items || [])];
    items[i] = { ...items[i], [key]: v };
    set({ ...data, items });
  };
  const addItem = () => set({ ...data, items: [...(data.items || []), { title: 'New feature', description: 'Brief benefit' }] });
  const removeItem = i => set({ ...data, items: (data.items || []).filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-4">
      <InlineText value={data.headline} onChange={v => set({ ...data, headline: v })} className="block text-2xl font-bold text-white text-center" as="h2" placeholder="Features headline" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {(data.items || []).map((f, i) => (
          <div key={i} className="p-4 rounded-xl relative group" style={{ background: '#161616', border: '1px solid rgba(255,255,255,0.04)' }}>
            <button onClick={() => removeItem(i)} className="absolute top-1.5 right-1.5 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-white/10"><Trash size={11} style={{ color: '#ff6b6b' }} /></button>
            <ImageUpload url={f.image_url || ''} onChange={v => updateItem(i, 'image_url', v)} testid={`feature-image-${i}`} label="Image" />
            <InlineText value={f.title} onChange={v => updateItem(i, 'title', v)} className="block text-sm font-semibold text-white mt-3" as="h3" placeholder="Feature title" />
            <InlineText value={f.description} onChange={v => updateItem(i, 'description', v)} multiline className="block text-xs text-white/55 mt-1.5" as="p" placeholder="One-line benefit" />
          </div>
        ))}
      </div>
      <button onClick={addItem} className="text-xs flex items-center gap-1 px-3 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', color: 'rgba(245,240,232,0.6)' }}>
        <Plus size={11} /> Add feature
      </button>
    </div>
  );
};

const SocialProofEditor = ({ data, set }) => {
  // support both `items` (preferred new shape) and legacy `testimonial`
  const items = data.items || (data.testimonial ? [data.testimonial] : []);
  const updateItem = (i, key, v) => {
    const newItems = [...items];
    newItems[i] = { ...newItems[i], [key]: v };
    set({ ...data, items: newItems, testimonial: undefined });
  };
  const addItem = () => set({ ...data, items: [...items, { quote: 'Loved working with them.', name: '[Customer Name]', role: '[Business]' }], testimonial: undefined });
  const removeItem = i => set({ ...data, items: items.filter((_, idx) => idx !== i), testimonial: undefined });

  return (
    <div className="space-y-4">
      <InlineText value={data.headline} onChange={v => set({ ...data, headline: v })} className="block text-2xl font-bold text-white text-center" as="h2" placeholder="What customers say" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map((t, i) => (
          <div key={i} className="p-4 rounded-xl relative group" style={{ background: '#161616', border: '1px solid rgba(255,255,255,0.04)' }}>
            <button onClick={() => removeItem(i)} className="absolute top-1.5 right-1.5 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-white/10"><Trash size={11} style={{ color: '#ff6b6b' }} /></button>
            <InlineText value={t.quote} onChange={v => updateItem(i, 'quote', v)} multiline className="block text-sm italic text-white/85" as="p" placeholder="Customer quote" />
            <div className="mt-2 text-[11px]">
              <InlineText value={t.name || t.author || ''} onChange={v => updateItem(i, 'name', v)} className="block font-semibold text-white" as="span" placeholder="[Customer Name]" />
              <InlineText value={t.role || ''} onChange={v => updateItem(i, 'role', v)} className="block text-white/50" as="span" placeholder="[Business / Role]" />
            </div>
          </div>
        ))}
      </div>
      <button onClick={addItem} className="text-xs flex items-center gap-1 px-3 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', color: 'rgba(245,240,232,0.6)' }}>
        <Plus size={11} /> Add testimonial
      </button>
    </div>
  );
};

const PricingEditor = ({ data, set }) => {
  const tiers = data.tiers || [
    { name: 'Starter', price: 'Free', sub: 'forever', features: ['Feature 1', 'Feature 2'], cta_text: 'Start free', cta_url: '' },
    { name: 'Pro', price: '$29', sub: '/month', features: ['Everything in Starter', 'Pro feature'], cta_text: 'Get Pro', cta_url: '', popular: true },
  ];
  const updateTier = (i, key, v) => {
    const t = [...tiers];
    t[i] = { ...t[i], [key]: v };
    set({ ...data, tiers: t });
  };
  const updateFeature = (i, j, v) => {
    const t = [...tiers];
    t[i] = { ...t[i], features: [...(t[i].features || [])] };
    t[i].features[j] = v;
    set({ ...data, tiers: t });
  };
  const addFeature = (i) => {
    const t = [...tiers];
    t[i] = { ...t[i], features: [...(t[i].features || []), 'New benefit'] };
    set({ ...data, tiers: t });
  };
  const removeTier = (i) => set({ ...data, tiers: tiers.filter((_, idx) => idx !== i) });
  const addTier = () => set({ ...data, tiers: [...tiers, { name: 'New', price: '$', sub: '/month', features: [], cta_text: 'Choose', cta_url: '' }] });

  return (
    <div className="space-y-4">
      <InlineText value={data.headline || 'Pricing'} onChange={v => set({ ...data, headline: v })} className="block text-2xl font-bold text-white text-center" as="h2" />
      <InlineText value={data.subheadline || ''} onChange={v => set({ ...data, subheadline: v })} className="block text-sm text-white/50 text-center" as="p" placeholder="Optional subheadline" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {tiers.map((t, i) => (
          <div key={i} className="p-4 rounded-xl relative" style={{ background: '#161616', border: t.popular ? '1px solid rgba(200,240,96,0.3)' : '1px solid rgba(255,255,255,0.04)' }}>
            <button onClick={() => removeTier(i)} className="absolute top-1.5 right-1.5 p-1 rounded hover:bg-white/10"><Trash size={11} style={{ color: '#ff6b6b' }} /></button>
            <InlineText value={t.name} onChange={v => updateTier(i, 'name', v)} className="block text-xs font-semibold text-white" as="p" />
            <div className="flex items-baseline gap-1 mt-1">
              <InlineText value={t.price} onChange={v => updateTier(i, 'price', v)} className="text-2xl font-bold text-white" as="span" />
              <InlineText value={t.sub} onChange={v => updateTier(i, 'sub', v)} className="text-xs text-white/40" as="span" />
            </div>
            <ul className="space-y-1 my-3">
              {(t.features || []).map((f, j) => (
                <li key={j} className="text-[11px] text-white/70">
                  <InlineText value={f} onChange={v => updateFeature(i, j, v)} as="span" />
                </li>
              ))}
            </ul>
            <button onClick={() => addFeature(i)} className="text-[10px] text-white/40 hover:text-white">+ feature</button>
            <div className="mt-3 space-y-1.5">
              <InlineText value={t.cta_text} onChange={v => updateTier(i, 'cta_text', v)} className="block text-center text-xs font-semibold px-3 py-1.5 rounded-lg" style={{ background: t.popular ? '#c8f060' : 'rgba(255,255,255,0.04)', color: t.popular ? '#080808' : '#fff' }} as="span" />
              <div className="flex items-center gap-1 text-[10px] text-white/40">
                <LinkSimple size={9} />
                <InlineText value={t.cta_url || ''} onChange={v => updateTier(i, 'cta_url', v)} className="text-[10px]" placeholder="Button URL" as="span" />
              </div>
            </div>
            <label className="flex items-center gap-1 mt-2 text-[10px] text-white/40 cursor-pointer">
              <input type="checkbox" checked={!!t.popular} onChange={e => updateTier(i, 'popular', e.target.checked)} />
              Mark as popular
            </label>
          </div>
        ))}
      </div>
      <button onClick={addTier} className="text-xs flex items-center gap-1 px-3 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', color: 'rgba(245,240,232,0.6)' }}>
        <Plus size={11} /> Add pricing tier
      </button>
    </div>
  );
};

const CtaEditor = ({ data, set }) => (
  <div className="space-y-3 text-center">
    <InlineText value={data.headline} onChange={v => set({ ...data, headline: v })} className="block text-2xl font-bold text-white" as="h2" />
    <InlineText value={data.subheadline} onChange={v => set({ ...data, subheadline: v })} multiline className="block text-sm text-white/60 max-w-md mx-auto" as="p" />
    <div className="flex flex-col items-center gap-2 mt-2">
      <InlineText value={data.button_text || data.cta_text || 'Get Started'} onChange={v => set({ ...data, button_text: v })} className="inline-block px-6 py-2.5 rounded-full text-sm font-semibold" style={{ background: '#c8f060', color: '#080808' }} as="span" />
      <div className="flex items-center gap-1.5 text-[11px] text-white/40">
        <LinkSimple size={11} />
        <InlineText value={data.cta_url || ''} onChange={v => set({ ...data, cta_url: v })} className="text-[11px] text-white/60" placeholder="https://your-link.com" as="span" />
      </div>
    </div>
    <InlineText value={data.guarantee || ''} onChange={v => set({ ...data, guarantee: v })} className="block text-[11px] text-white/30" as="p" placeholder="Optional reassurance" />
  </div>
);

const FaqEditor = ({ data, set }) => {
  const updateItem = (i, key, v) => {
    const items = [...(data.items || [])];
    items[i] = { ...items[i], [key]: v };
    set({ ...data, items });
  };
  const addItem = () => set({ ...data, items: [...(data.items || []), { question: 'New question?', answer: 'Answer.' }] });
  const removeItem = i => set({ ...data, items: (data.items || []).filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-3">
      <InlineText value={data.headline || 'Frequently asked'} onChange={v => set({ ...data, headline: v })} className="block text-2xl font-bold text-white text-center" as="h2" />
      {(data.items || []).map((q, i) => (
        <div key={i} className="p-3 rounded-xl relative group" style={{ background: '#161616', border: '1px solid rgba(255,255,255,0.04)' }}>
          <button onClick={() => removeItem(i)} className="absolute top-2 right-2 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-white/10"><Trash size={11} style={{ color: '#ff6b6b' }} /></button>
          <InlineText value={q.question} onChange={v => updateItem(i, 'question', v)} className="block text-sm font-semibold text-white" as="p" placeholder="Question" />
          <InlineText value={q.answer} onChange={v => updateItem(i, 'answer', v)} multiline className="block text-xs text-white/55 mt-1.5" as="p" placeholder="Answer" />
        </div>
      ))}
      <button onClick={addItem} className="text-xs flex items-center gap-1 px-3 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', color: 'rgba(245,240,232,0.6)' }}>
        <Plus size={11} /> Add Q&A
      </button>
    </div>
  );
};

/* ----- main editor ----- */
export default function LaunchPageEditor({ sections, pageId, onChange, onPublished }) {
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishedUrl, setPublishedUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [slug, setSlug] = useState('');

  if (!sections) return null;

  // initialize section order if missing
  const orderedKeys = [...Object.keys(SECTION_META)]
    .filter(k => sections[k] !== undefined)
    .sort((a, b) => (sections[a]?._order ?? SECTION_META[a].order) - (sections[b]?._order ?? SECTION_META[b].order));

  const updateSection = (name, newData) => {
    onChange({ ...sections, [name]: newData });
  };

  const moveSection = (name, direction) => {
    const idx = orderedKeys.indexOf(name);
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= orderedKeys.length) return;
    const next = { ...sections };
    const otherName = orderedKeys[newIdx];
    const aOrder = next[name]?._order ?? SECTION_META[name].order;
    const bOrder = next[otherName]?._order ?? SECTION_META[otherName].order;
    next[name] = { ...(next[name] || {}), _order: bOrder };
    next[otherName] = { ...(next[otherName] || {}), _order: aOrder };
    onChange(next);
  };

  const toggleVisible = (name) => {
    updateSection(name, { ...(sections[name] || {}), visible: sections[name]?.visible === false });
  };

  const saveDraft = async () => {
    if (!pageId) { toast.error('Generate a page first'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/api/launch-page/${pageId}/sections`, { sections });
      toast.success('Draft saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    if (!pageId) { toast.error('Generate a page first'); return; }
    setPublishing(true);
    try {
      // Save current edits first
      await axios.put(`${API}/api/launch-page/${pageId}/sections`, { sections });
      const payload = slug ? { slug } : {};
      const { data } = await axios.post(`${API}/api/launch-page/${pageId}/publish`, payload);
      setPublishedUrl(data.url || data.published_url);
      toast.success('Your page is live!');
      onPublished?.(data);
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Publish failed');
    } finally { setPublishing(false); }
  };

  const unpublish = async () => {
    if (!pageId) return;
    try {
      await axios.post(`${API}/api/launch-page/${pageId}/unpublish`);
      setPublishedUrl('');
      toast.success('Unpublished');
    } catch { toast.error('Unpublish failed'); }
  };

  const copyUrl = async () => {
    if (!publishedUrl) return;
    await navigator.clipboard.writeText(publishedUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const RENDERERS = {
    hero: HeroEditor, problem: ProblemEditor, solution: SolutionEditor, features: FeaturesEditor,
    social_proof: SocialProofEditor, pricing: PricingEditor, cta: CtaEditor, faq: FaqEditor,
  };

  return (
    <div className="space-y-4" data-testid="launch-page-editor">
      {/* Action bar */}
      <div className="sticky top-0 z-20 flex items-center justify-between flex-wrap gap-3 px-4 py-3 rounded-xl backdrop-blur"
           style={{ background: 'rgba(20,20,20,0.9)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <p className="text-xs" style={{ color: 'rgba(245,240,232,0.5)' }}>
          Click any text to edit · Drag images to replace · Toggle <Eye size={11} className="inline align-baseline" /> to hide a section
        </p>
        <div className="flex items-center gap-2">
          <button onClick={saveDraft} disabled={saving} data-testid="lpe-save-draft"
            className="px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"
            style={{ background: 'rgba(255,255,255,0.05)', color: '#f5f0e8', border: '1px solid rgba(255,255,255,0.06)' }}>
            <FloppyDisk size={12} weight="bold" /> {saving ? 'Saving…' : 'Save draft'}
          </button>
          <button onClick={publish} disabled={publishing} data-testid="lpe-publish"
            className="px-4 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"
            style={{ background: '#c8f060', color: '#080808' }}>
            <Globe size={12} weight="bold" /> {publishing ? 'Publishing…' : (publishedUrl ? 'Republish' : 'Publish page')}
          </button>
        </div>
      </div>

      {publishedUrl && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} data-testid="lpe-published-banner"
          className="flex items-center gap-3 px-4 py-3 rounded-xl"
          style={{ background: 'rgba(200,240,96,0.08)', border: '1px solid rgba(200,240,96,0.2)' }}>
          <CheckCircle size={16} weight="fill" style={{ color: '#c8f060' }} />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold" style={{ color: '#c8f060' }}>Your page is live</p>
            <a href={publishedUrl} target="_blank" rel="noreferrer" className="text-[11px] truncate block hover:underline" style={{ color: 'rgba(245,240,232,0.7)' }} data-testid="lpe-published-url">{publishedUrl}</a>
          </div>
          <button onClick={copyUrl} className="px-2.5 py-1.5 rounded-md text-[11px] font-medium flex items-center gap-1" style={{ background: 'rgba(255,255,255,0.05)', color: '#f5f0e8' }}>
            {copied ? <><CheckCircle size={11} weight="fill" /> Copied</> : <><Copy size={11} /> Copy</>}
          </button>
          <button onClick={unpublish} className="text-[11px] px-2.5 py-1.5 rounded-md hover:bg-white/5" style={{ color: 'rgba(245,240,232,0.5)' }} data-testid="lpe-unpublish">Unpublish</button>
        </motion.div>
      )}

      {/* Slug input */}
      <div className="px-4 py-2 rounded-xl flex items-center gap-2" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
        <span className="text-[11px]" style={{ color: 'rgba(245,240,232,0.4)' }}>Custom URL:</span>
        <span className="text-[11px]" style={{ color: 'rgba(245,240,232,0.5)' }}>scalitos.com/p/</span>
        <input value={slug} onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').slice(0, 40))}
          placeholder="your-page" data-testid="lpe-slug"
          className="flex-1 bg-transparent border-none outline-none text-[11px]" style={{ color: '#f5f0e8' }} />
      </div>

      {orderedKeys.map((name, i) => {
        const Editor = RENDERERS[name];
        if (!Editor) return null;
        return (
          <SectionShell key={name} name={name} section={sections[name]}
            onUpdate={updateSection} onMove={moveSection} onToggleVisible={toggleVisible}
            isFirst={i === 0} isLast={i === orderedKeys.length - 1}>
            <Editor data={sections[name] || {}} set={data => updateSection(name, data)} />
          </SectionShell>
        );
      })}
    </div>
  );
}
